import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { Form, Input, Space } from "antd";
import { useState } from "react";
import { useCreateProject } from "@/common/artemis-operations/projects";
import {
	ERROR_PROJECT_CREATION_DESC,
	ERROR_PROJECT_CREATION_TITLE,
	PROJECT_CREATED_DESC,
	PROJECT_CREATED_TITLE,
} from "@/common/messages";
import { ProjectType } from "@/common/model";
import { useNotify } from "@/common/use-notify";
import { Wizard } from "@/components/wizard";

interface ProjectCreateWizardProps {
	open: boolean;
	onClose: () => void;
}

/** Wizard for creating a new project. */
export function ProjectCreateWizard({
	open,
	onClose,
}: ProjectCreateWizardProps) {
	const { workspaceId } = useParams({ strict: false }) as {
		workspaceId: string;
	};
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const queryClient = useQueryClient();
	const createProject = useCreateProject();
	const notify = useNotify();

	const reset = () => {
		form.resetFields();
		setLoading(false);
	};
	const handleComplete = () => {
		form.validateFields().then((values) => {
			setLoading(true);
			createProject(
				{
					name: values.name,
					description: values.description ?? "",
					type: values.type ?? ProjectType.SOFTWARE_APPLICATION,
					workspaceId,
				},
				{
					onSuccess: () => {
						notify.success(PROJECT_CREATED_TITLE, PROJECT_CREATED_DESC);
						queryClient.invalidateQueries({ queryKey: ["projects"] });
						onClose();
						reset();
					},
					onError: () => {
						setLoading(false);
						notify.error(
							ERROR_PROJECT_CREATION_TITLE,
							ERROR_PROJECT_CREATION_DESC,
						);
					},
				},
			);
		});
	};

	return (
		<Wizard
			open={open}
			title="Create Project"
			completeLabel="Create"
			form={form}
			loading={loading}
			onComplete={handleComplete}
			onCancel={() => {
				reset();
				onClose();
			}}
			pages={[
				{
					id: "details",
					title: "Details",
					content: (
						<Space
							orientation="vertical"
							size={"small"}
							style={{ width: "100%" }}
						>
							<Form.Item name="name" label="Name" rules={[{ required: true }]}>
								<Input placeholder="Project name" />
							</Form.Item>
							<Form.Item name="description" label="Description">
								<Input.TextArea rows={3} placeholder="Describe the project" />
							</Form.Item>
						</Space>
					),
				},
			]}
		/>
	);
}
